import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'grommet';
import { Box, Input, Select, Text } from '/src/components';
import { TUserId, User } from '/src/lib/models';

export type ContactsListProps = {
  contacts?: User[];
  isUpdatePage?: boolean;
  isCorporate?: boolean;
  isRemittance?: boolean;
  isFacility?: boolean;
};

export const ContactsList: React.FC<ContactsListProps> = (props) => {
  const { contacts = [], isUpdatePage, isCorporate, isRemittance, isFacility } = props;
  const { t } = useTranslation();

  /** State **/
  const [selectedContactId, setSelectedContactId] = useState<TUserId>();

  /** Computed **/
  const isClient = isCorporate || isRemittance;

  const entityName = isClient ? 'Client' : isFacility ? 'Facility' : undefined;
  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) ?? contacts[0];

  const formatPhoneNumber = (value: string) => {
    const digitsOnly = value.replace(/[^\d]/g, '');

    if (!digitsOnly) return '';

    if (digitsOnly.length >= 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
    return value;
  };

  useEffect(() => {
    if (!!contacts?.length && !selectedContactId) {
      setSelectedContactId(contacts[0]?.id);
    }
  }, [contacts, selectedContactId]);

  return (
    <Box gap="1rem" fill="horizontal">
      {!isUpdatePage && (
        <Box
          pad="1rem"
          gap="0.5rem"
          background="light-6"
          border={{ size: 'small', color: 'light-5' }}
          borderRadius="6px"
          fontFamily="Lato, sans-serif"
        >
          <Text size="large">{t('contacts:pointsOfContact')}</Text>
          <Text size="medium">{t('contacts:pointsOfContactDescription', { entityType: entityName })}</Text>
        </Box>
      )}
      {isUpdatePage && (
        <Box gap="1rem">
          <Box gap="small">
            <Text color="dark-1" size="large" weight={300}>
              {t('contacts:pointsOfContact')}
            </Text>
            <Box>
              <Text fontFamily="Lato, sans-serif">
                {t('contacts:pointsOfContactDescriptionClient', { entityType: entityName })}
              </Text>
            </Box>
          </Box>
          <Select
            id="contact_name"
            label={t('contacts:contact')}
            placeholder={t('contacts:choose')}
            value={selectedContactId}
            setValue={(value) => setSelectedContactId(value)}
            options={contacts.map((user) => ({ label: user.name, value: user.id }))}
            hideOptionalText
          />
          <Box height="8rem">
            {selectedContact && (
              <Box gap="1rem">
                <Button plain>
                  <Input
                    id="contact_role_name"
                    label={t('contacts:contactRole')}
                    value={selectedContact.role.name}
                    placeholder=""
                    setValue={() => {}}
                    hideOptionalText
                    disabled
                  />
                </Button>
                <Button plain>
                  <Input
                    id="contact_email"
                    label={t('contacts:contactEmail')}
                    value={selectedContact.email}
                    placeholder=""
                    setValue={() => {}}
                    hideOptionalText
                    disabled
                  />
                </Button>
                <Button plain>
                  <Input
                    id="contact_phone"
                    label={t('contacts:contactPhone')}
                    value={selectedContact.phone ? formatPhoneNumber(selectedContact.phone) : ''}
                    placeholder=""
                    setValue={() => {}}
                    hideOptionalText
                    width="1/3"
                    disabled
                  />
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
